import { useEffect, useState } from "react";
import React from "react";
import { http } from "@/lib/api";
import { canAct } from "@/lib/access";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
  FileSpreadsheet, FileText, Wallet, ChevronDown, ChevronRight,
  Receipt, TrendingUp, Lock, LockOpen, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { fmtNum, fmtDate, MONTH_NAMES, ROLE_LABELS, roleBadgeClass } from "@/lib/helpers";

export default function Payroll() {
  const { user } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [split, setSplit] = useState("proportional");
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  const canManage = user?.role === "koordinator" || user?.role === "patron";
  const isPersonel = user?.role === "personel";

  const load = async () => {
    const { data } = await http.get(`/payroll?year=${year}&month=${month}&split_mode=${split}`);
    setData(data);
    if (isPersonel && data.rows.length > 0) setExpanded({ [data.rows[0].user_id]: true });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, split]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const downloadFile = async (fmt) => {
    const res = await http.get(`/payroll/export/${fmt}?year=${year}&month=${month}&split_mode=${split}`, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `bordro_${year}_${String(month).padStart(2,"0")}.${fmt === "excel" ? "xlsx" : "pdf"}`;
    a.click();
    URL.revokeObjectURL(blobUrl);
  };

  const lock = async () => {
    try {
      await http.post(`/payroll/lock?year=${year}&month=${month}&split_mode=${split}`);
      toast.success("Bordro kilitlendi. Hakedişler personel cüzdanlarına işlendi.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kilitlenemedi");
    }
  };

  const unlock = async () => {
    try {
      await http.delete(`/payroll/lock?year=${year}&month=${month}`);
      toast.success("Kilit kaldırıldı. Cüzdan kayıtları silindi.");
      load();
    } catch (e) {
      toast.error("Kilit kaldırılamadı");
    }
  };

  if (!data) return <div className="text-slate-400">Yükleniyor...</div>;

  return (
    <div className="space-y-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2">
            <Wallet className="text-amber-400" /> {isPersonel ? "Hakediş Özetim" : "Hakediş Bordrosu"}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {isPersonel
              ? "Bu ay içinde kazandığın tüm kampanya hakedişleri."
              : "Personel kampanya kazançlarını tek ekranda görün, kilitleyin ve dışa aktarın."}
          </p>
          {data.locked && (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs font-semibold" data-testid="lock-badge">
              <Lock size={12} /> Kilitli · {fmtDate(data.locked_at)}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!isPersonel && !data.locked && (
            <Select value={split} onValueChange={setSplit}>
              <SelectTrigger className="bg-slate-900/60 border-slate-700 w-56" data-testid="payroll-split">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="proportional">Oransal (puan payına göre)</SelectItem>
                <SelectItem value="equal">Eşit paylaşım</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-32" data-testid="payroll-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="bg-slate-900/60 border-slate-700 w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{[2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          {canAct(user, "export") && (<><Button onClick={() => downloadFile("excel")} className="bg-emerald-600 hover:bg-emerald-500" data-testid="payroll-export-excel">
            <FileSpreadsheet size={16} className="mr-1" /> Excel
          </Button>
          <Button onClick={() => downloadFile("pdf")} className="bg-red-600 hover:bg-red-500" data-testid="payroll-export-pdf">
            <FileText size={16} className="mr-1" /> PDF
          </Button></>)}
          {canManage && !data.locked && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="bg-amber-600 hover:bg-amber-500" data-testid="payroll-lock-btn">
                  <ShieldCheck size={16} className="mr-1" /> Onayla & Kilitle
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-slate-800">
                <AlertDialogHeader>
                  <AlertDialogTitle>Bu ayı kilitlemek istediğinize emin misiniz?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    Bu işlemden sonra bordro değiştirilemez. Her personelin tutarı cüzdanına işlenir. Kilidi yalnızca koordinatör kaldırabilir.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-slate-700 bg-slate-800">İptal</AlertDialogCancel>
                  <AlertDialogAction onClick={lock} className="bg-amber-600 hover:bg-amber-500" data-testid="payroll-lock-confirm">
                    Kilitle
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {user?.role === "koordinator" && data.locked && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-slate-700 bg-slate-800/50" data-testid="payroll-unlock-btn">
                  <LockOpen size={16} className="mr-1" /> Kilidi Aç
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-slate-800">
                <AlertDialogHeader>
                  <AlertDialogTitle>Kilidi açmak istiyor musunuz?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    Cüzdan kayıtları da silinir ve bordro yeniden hesaplanabilir hale gelir.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-slate-700 bg-slate-800">İptal</AlertDialogCancel>
                  <AlertDialogAction onClick={unlock} className="bg-red-600 hover:bg-red-500">Kilidi Aç</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-3 animate-fade-up delay-100">
        <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-5">
          <p className="text-[10px] uppercase font-mono text-amber-200">
            {isPersonel ? "Toplam Hakedişim" : "Genel Toplam"}
          </p>
          <p className="font-display text-3xl font-bold text-amber-300 mt-1">{fmtNum(data.grand_total)}₺</p>
          <p className="text-xs text-slate-400 mt-1">{MONTH_NAMES[month-1]} {year}</p>
        </div>
        {!isPersonel && (
          <>
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 glass p-5">
              <p className="text-[10px] uppercase font-mono text-slate-500">Hak Eden Personel</p>
              <p className="font-display text-3xl font-bold mt-1">{data.rows.filter((r) => r.total_earning > 0).length}</p>
              <p className="text-xs text-slate-500 mt-1">/ {data.rows.length} toplam</p>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 glass p-5">
              <p className="text-[10px] uppercase font-mono text-slate-500">Ortalama</p>
              <p className="font-display text-3xl font-bold text-blue-300 mt-1">
                {fmtNum(data.rows.length > 0 ? Math.round(data.grand_total / data.rows.length) : 0)}₺
              </p>
              <p className="text-xs text-slate-500 mt-1">personel başına</p>
            </div>
          </>
        )}
      </div>

      <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Receipt size={16} className="text-blue-400" /> {isPersonel ? "Kampanya Kazançlarım" : "Personel Bazlı Bordro"}
          </CardTitle>
          {!isPersonel && <p className="text-xs text-slate-500">Satıra tıklayarak kampanya kırılımını açın.</p>}
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                {!isPersonel && <TableHead></TableHead>}
                <TableHead>{isPersonel ? "Kampanya / Kaynak" : "Personel"}</TableHead>
                {!isPersonel && <TableHead>Şube</TableHead>}
                {!isPersonel && <TableHead>Rol</TableHead>}
                <TableHead className="text-right">Adet</TableHead>
                {!isPersonel && <TableHead className="text-right">Puan</TableHead>}
                <TableHead className="text-right">Hakediş</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPersonel && data.rows.length > 0 && data.rows[0].earnings.map((e, i) => (
                <TableRow key={i} className="border-slate-800">
                  <TableCell>
                    <div className="font-semibold text-sm">{e.campaign_name}</div>
                    <div className="text-[10px] text-slate-500">
                      {e.scope === "branch" ? "Şube paylaşımı" : "Bireysel"}
                      {e.tier_threshold != null && e.tier_threshold > 0 && ` · Kademe ≥ ${e.tier_threshold}`}
                      {e.scope === "branch" && ` · ${e.share_basis === "proportional" ? "oransal" : "eşit"}`}
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-slate-300">{fmtNum(e.qty)}</TableCell>
                  <TableCell className="text-right font-bold text-amber-300">+{fmtNum(e.amount)}₺</TableCell>
                </TableRow>
              ))}
              {isPersonel && (data.rows.length === 0 || data.rows[0].earnings.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-slate-500 py-10">
                    Bu ay henüz hakediş yok. Hadi satışa! 💪
                  </TableCell>
                </TableRow>
              )}
              {!isPersonel && data.rows.map((r) => (
                <React.Fragment key={r.user_id}>
                  <TableRow
                    className={`border-slate-800 cursor-pointer ${r.total_earning > 0 ? "" : "opacity-60"}`}
                    onClick={() => r.earnings.length > 0 && toggle(r.user_id)}
                    data-testid={`payroll-row-${r.username}`}>
                    <TableCell className="w-10">
                      {r.earnings.length > 0 && (expanded[r.user_id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                    </TableCell>
                    <TableCell className="font-semibold">
                      {r.full_name}
                      <div className="text-[10px] font-mono text-slate-500">{r.username}</div>
                    </TableCell>
                    <TableCell className="text-sm">{r.branch_name}</TableCell>
                    <TableCell>
                      <Badge className={roleBadgeClass(r.role)}>{ROLE_LABELS[r.role]}</Badge>
                    </TableCell>
                    <TableCell className="text-right text-slate-300">{fmtNum(r.qty)}</TableCell>
                    <TableCell className="text-right text-slate-300">{fmtNum(r.points)}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-bold font-display ${r.total_earning > 0 ? "text-amber-300" : "text-slate-600"}`}>
                        {fmtNum(r.total_earning)}₺
                      </span>
                    </TableCell>
                  </TableRow>
                  {expanded[r.user_id] && r.earnings.map((e, i) => (
                    <TableRow key={`${r.user_id}-${i}`} className="border-slate-800 bg-slate-950/40">
                      <TableCell></TableCell>
                      <TableCell colSpan={2}>
                        <div className="text-xs text-slate-300 font-medium">{e.campaign_name}</div>
                        <div className="text-[10px] text-slate-500">
                          {e.scope === "branch" ? "Şube paylaşımı" : "Bireysel"}
                          {e.tier_threshold != null && e.tier_threshold > 0 && ` · Kademe ≥ ${e.tier_threshold}`}
                          {e.scope === "branch" && ` · ${e.share_basis === "proportional" ? "oransal" : "eşit"}`}
                        </div>
                      </TableCell>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell className="text-right text-xs text-slate-400">{fmtNum(e.qty)} adet</TableCell>
                      <TableCell className="text-right text-amber-300 font-semibold text-sm">+{fmtNum(e.amount)}₺</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
              {!isPersonel && data.rows.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10"><Wallet className="mx-auto mb-2" /> Personel bulunamadı</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!isPersonel && data.by_branch.length > 0 && (
        <Card className="border-slate-800 bg-slate-900/60 glass animate-fade-up delay-300">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp size={16} className="text-emerald-400" /> Şube Bazlı Toplam
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead>Şube</TableHead>
                  <TableHead className="text-right">Personel</TableHead>
                  <TableHead className="text-right">Toplam Hakediş</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.by_branch.map((b) => (
                  <TableRow key={b.branch_id || "none"} className="border-slate-800">
                    <TableCell>
                      <div className="font-semibold">{b.branch_name}</div>
                      <div className="text-[10px] font-mono text-slate-500">{b.branch_code}</div>
                    </TableCell>
                    <TableCell className="text-right">{b.employees.length}</TableCell>
                    <TableCell className="text-right font-bold text-amber-300">{fmtNum(b.total)}₺</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
